import { useState, lazy, Suspense, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth } from '@features/auth';
import { Sidebar } from './components/Sidebar';
import { MobileHeader } from './components/MobileHeader';
import { DesktopTopBar } from './components/DesktopTopBar';
import { MobileBottomNav } from './components/MobileBottomNav';
import { AppRoutes } from './routes/AppRoutes';
import { MobileMenuProvider } from '@/shared/contexts/MobileMenuContext';
import { Toaster } from '@/shared/components/ui/sonner';
import { useKeyboardShortcuts, useAppUpdate } from '@/shared/hooks';
import { CelebrationProvider } from '@/shared/providers/CelebrationProvider';
import { StreakReminderManager } from '@/shared/components/StreakReminderManager';
import { AppErrorBoundary } from '@/shared/components/AppErrorBoundary';

// Lazy load modals - only loaded when needed
const OnboardingManager = lazy(() =>
  import('@/shared/components/onboarding').then((m) => ({ default: m.OnboardingManager }))
);
const KeyboardShortcutsModal = lazy(() =>
  import('@/shared/components/KeyboardShortcutsModal').then((m) => ({ default: m.KeyboardShortcutsModal }))
);

function App() {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  useAppUpdate();

  // Set Sentry user context
  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({ id: user.id, email: user.email, role: user.role });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated, user]);

  // Public routes where sidebar should not be shown
  const isPublicRoute = location.pathname === '/login' || location.pathname.startsWith('/register');

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: '?',
      action: () => setShowShortcutsModal(true),
      description: 'Mostrar atajos de teclado',
    },
    {
      key: 'g d',
      action: () => navigate('/dashboard'),
      description: 'Ir a Dashboard',
    },
    {
      key: 'g l',
      action: () => navigate('/lms'),
      description: 'Ir a LMS',
    },
    {
      key: 'g f',
      action: () => navigate('/forum'),
      description: 'Ir a Foro',
    },
    {
      key: 'Escape',
      action: () => setShowShortcutsModal(false),
      description: 'Cerrar modal',
    },
  ]);

  return (
    <AppErrorBoundary>
    <CelebrationProvider>
    <div className="min-h-screen bg-[#F8F9FB] dark:bg-gray-950 transition-colors">
      {/* Skip Links for Accessibility */}
      <a href="#main-content" className="skip-link">
        Saltar al contenido principal
      </a>
      <a href="#main-navigation" className="skip-link" style={{ left: '200px' }}>
        Saltar a navegación
      </a>

      {isAuthenticated && !isPublicRoute && user ? (
        <MobileMenuProvider>
          <div className="flex">
            <Sidebar user={user} onLogout={logout} />
            <MobileHeader user={user} />
            <DesktopTopBar />
            <main
              id="main-content"
              className="flex-1 pt-16 lg:pt-0 pb-20 lg:pb-0 overflow-auto"
              role="main"
              tabIndex={-1}
              style={{ height: '100vh' }}
            >
              <AppRoutes />
            </main>
            <MobileBottomNav />
          </div>

          {/* Onboarding system for first-time users - lazy loaded */}
          <Suspense fallback={null}>
            <OnboardingManager />
          </Suspense>

          {/* Streak reminder for students - activates notifications when streak is at risk */}
          <StreakReminderManager />
        </MobileMenuProvider>
      ) : (
        <main id="main-content" role="main" tabIndex={-1}>
          <AppRoutes />
        </main>
      )}

      {/* Keyboard shortcuts modal - lazy loaded */}
      {showShortcutsModal && (
        <Suspense fallback={null}>
          <KeyboardShortcutsModal
            isOpen={showShortcutsModal}
            onClose={() => setShowShortcutsModal(false)}
          />
        </Suspense>
      )}

      {/* Toast notifications */}
      <Toaster />

    </div>
    </CelebrationProvider>
    </AppErrorBoundary>
  );
}

export default App;
